import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../utils/api';
import SignatureCanvas from 'react-signature-canvas';

const SettlementReviewPage = () => {
  const { disputeId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const signaturePadRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [settlement, setSettlement] = useState(null);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [signed, setSigned] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    // Extract partyId from URL and store in localStorage
    const partyIdFromUrl = searchParams.get('partyId');
    if (partyIdFromUrl) {
      localStorage.setItem('partyId', partyIdFromUrl);
    }

    loadSettlement();
  }, [disputeId, searchParams]);

  const loadSettlement = async () => {
    try {
      setLoading(true);

      // Get partyId to pass to API
      const partyId = localStorage.getItem('partyId');

      // Get settlement by dispute ID using api utility
      const data = await api.request(`/disputes/${disputeId}/settlement?partyId=${partyId || ''}`);
      setSettlement(data);

      // Use the API's determination of whether THIS party has accepted
      setAccepted(data.currentPartyAccepted || false);
    } catch (err) {
      console.error('Load settlement error:', err);
      setError(err.message || 'Failed to load settlement');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!window.confirm('Do you accept this settlement agreement?')) {
      return;
    }

    try {
      setProcessing(true);

      const partyId = localStorage.getItem('partyId');
      if (!partyId) {
        throw new Error('Party ID not found. Please log in again.');
      }

      const result = await api.request(`/settlement/${settlement.settlementId}/accept`, {
        method: 'POST',
        body: JSON.stringify({
          partyId,
          accepted: true
        })
      });

      if (result.bothAccepted) {
        setAccepted(true);
        alert('Settlement accepted! Both parties have accepted. Please sign below to execute.');
      } else {
        alert('Settlement accepted! Waiting for the other party to accept.');
        setAccepted(true);
      }

      await loadSettlement();
    } catch (err) {
      console.error('Accept error:', err);
      alert(err.message || 'Failed to accept settlement. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm('Are you sure you want to reject this settlement? This will restart negotiations.')) {
      return;
    }

    try {
      setProcessing(true);

      const partyId = localStorage.getItem('partyId');
      await api.request(`/settlement/${settlement.settlementId}/accept`, {
        method: 'POST',
        body: JSON.stringify({
          partyId,
          accepted: false
        })
      });

      alert('Settlement rejected. The attorney will be notified.');
      navigate(-1);
    } catch (err) {
      console.error('Reject error:', err);
      alert(err.message || 'Failed to reject settlement. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleSign = async () => {
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
      alert('Please provide your signature');
      return;
    }

    if (!window.confirm('By signing, you agree to be legally bound by this settlement agreement.')) {
      return;
    }

    try {
      setProcessing(true);

      const partyId = localStorage.getItem('partyId');
      const signature = signaturePadRef.current.toDataURL();

      const result = await api.request(`/settlement/${settlement.settlementId}/sign`, {
        method: 'POST',
        body: JSON.stringify({
          partyId,
          signature,
          ipAddress: await fetch('https://api.ipify.org?format=json')
            .then(r => r.json())
            .then(d => d.ip)
            .catch(() => 'unknown')
        })
      });

      if (result.bothSigned) {
        setSigned(true);
        alert('Settlement signed and executed! Payment instructions will be sent shortly.');
      } else {
        setSigned(true);
        alert('Your signature has been recorded. Waiting for the other party to sign.');
      }

      await loadSettlement();
    } catch (err) {
      console.error('Sign error:', err);
      alert(err.message || 'Failed to sign settlement. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading settlement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="card max-w-md w-full">
          <div className="text-center">
            <div className="text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold mb-2">Error</h2>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const isExecuted = settlement.status === 'executed';
  const bothAccepted = settlement.status === 'accepted' || isExecuted;
  const currentPartyAccepted = accepted; // This party has accepted
  const waitingForOtherParty = currentPartyAccepted && !bothAccepted; // This party accepted but other hasn't

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold mb-3">Settlement Agreement</h1>
        <p className="text-gray-600">
          {isExecuted
            ? 'This settlement has been executed'
            : bothAccepted
            ? 'Ready for signature'
            : 'Review and accept this settlement proposal'}
        </p>
        <div className="mt-2 inline-block px-4 py-2 bg-blue-50 rounded-lg">
          <p className="text-sm text-gray-600">Status</p>
          <p className="font-bold capitalize">{settlement.status.replace(/_/g, ' ')}</p>
        </div>
      </div>

      {/* Settlement Amount */}
      <div className="card bg-green-50 border-2 border-green-300 mb-6">
        <div className="text-center">
          <h2 className="text-xl font-bold text-green-800 mb-2">Settlement Amount</h2>
          <p className="text-4xl font-bold text-green-600">
            {formatCurrency(settlement.amount)}
          </p>
        </div>
      </div>

      {/* Payment Structure */}
      <div className="card mb-6">
        <h3 className="text-xl font-bold mb-4">Payment Structure</h3>

        {settlement.structure.type === 'lump_sum' ? (
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">Lump Sum Payment</p>
                <p className="text-sm text-gray-600">Due within {settlement.structure.dueInDays} days</p>
              </div>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(settlement.amount)}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">Initial Payment</p>
                  <p className="text-sm text-gray-600">Due upon signing</p>
                </div>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(settlement.structure.initial)}
                </p>
              </div>
            </div>
            {settlement.structure.installments.map((inst, idx) => (
              <div key={idx} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">Installment {idx + 1}</p>
                    <p className="text-sm text-gray-600">Due in {inst.dueInDays} days</p>
                  </div>
                  <p className="text-2xl font-bold text-gray-700">
                    {formatCurrency(inst.amount)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Settlement Terms */}
      <div className="card mb-6">
        <h3 className="text-xl font-bold mb-4">Settlement Terms</h3>
        <div className="space-y-2">
          {settlement.terms.map((term, idx) => (
            <div key={idx} className="flex items-start">
              <span className="text-green-500 mr-2 mt-1">✓</span>
              <p className="text-gray-700">{formatTerm(term)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Document Preview */}
      {settlement.documentPreview && (
        <div className="card mb-6 bg-gray-50">
          <h3 className="text-xl font-bold mb-4">Legal Agreement</h3>
          <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono bg-white p-4 rounded border">
            {settlement.documentPreview}
          </pre>
        </div>
      )}

      {/* Signature Section - Only if BOTH parties accepted */}
      {bothAccepted && !isExecuted && !signed && (
        <div className="card mb-6 border-2 border-yellow-300">
          <h3 className="text-xl font-bold mb-4">Digital Signature Required</h3>
          <p className="text-sm text-gray-600 mb-4">
            Please sign below to execute this settlement agreement. Your signature is legally binding.
          </p>

          <div className="border-2 border-gray-300 rounded-lg bg-white mb-4">
            <SignatureCanvas
              ref={signaturePadRef}
              penColor="black"
              canvasProps={{
                className: 'w-full h-48',
                style: { touchAction: 'none' }
              }}
            />
          </div>

          <div className="flex justify-between items-center">
            <button
              onClick={clearSignature}
              className="btn-secondary"
              disabled={processing}
            >
              Clear Signature
            </button>
            <button
              onClick={handleSign}
              className="btn-primary"
              disabled={processing}
            >
              {processing ? 'Signing...' : 'Sign & Execute Settlement'}
            </button>
          </div>
        </div>
      )}

      {/* Action Buttons - Only if THIS party has not accepted yet */}
      {!currentPartyAccepted && !isExecuted && (
        <div className="card bg-yellow-50 border-2 border-yellow-300">
          <h3 className="font-bold mb-4">Your Decision Required</h3>
          <p className="text-sm text-gray-700 mb-4">
            Please review the settlement terms carefully. Once you accept, the other party will be notified.
            After both parties accept, you will both need to sign to execute the settlement.
          </p>

          <div className="flex gap-4">
            <button
              onClick={handleReject}
              className="btn-secondary flex-1"
              disabled={processing}
            >
              {processing ? 'Processing...' : 'Reject Settlement'}
            </button>
            <button
              onClick={handleAccept}
              className="btn-primary flex-1"
              disabled={processing}
            >
              {processing ? 'Processing...' : 'Accept Settlement'}
            </button>
          </div>
        </div>
      )}

      {/* Status Messages */}
      {waitingForOtherParty && !isExecuted && (
        <div className="card bg-blue-50 border-blue-200">
          <div className="flex items-start">
            <div className="text-2xl mr-3">⏳</div>
            <div>
              <h4 className="font-bold mb-2">Waiting for Other Party</h4>
              <p className="text-sm text-gray-700">
                You have accepted this settlement. We're waiting for the other party to accept.
                You'll receive an email when they respond.
              </p>
            </div>
          </div>
        </div>
      )}

      {signed && !isExecuted && (
        <div className="card bg-green-50 border-green-200">
          <div className="flex items-start">
            <div className="text-2xl mr-3">✅</div>
            <div>
              <h4 className="font-bold mb-2">Signature Recorded</h4>
              <p className="text-sm text-gray-700">
                Your signature has been recorded. Waiting for the other party to sign.
                Once both parties have signed, payment instructions will be sent.
              </p>
            </div>
          </div>
        </div>
      )}

      {isExecuted && (
        <div className="card bg-green-50 border-2 border-green-300">
          <div className="text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h3 className="text-2xl font-bold text-green-800 mb-2">Settlement Executed!</h3>
            <p className="text-gray-700 mb-4">
              This settlement agreement has been fully executed by both parties.
              Payment instructions have been sent to your email.
            </p>
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-600 mb-2">
          Questions about this settlement?
        </p>
        <p className="text-sm text-gray-500">
          Contact support@haladik.com
        </p>
      </div>

      <div className="mt-6 text-center text-xs text-gray-400">
        <p>
          🔒 Legally binding agreement • ⚖️ Attorney-approved • 🤖 Generated by TensionBridge ADR
        </p>
      </div>
    </div>
  );
};

// Helper function to format term names
function formatTerm(term) {
  const termNames = {
    'mutual_release': 'Mutual Release of All Claims - Neither party can pursue further legal action regarding this matter',
    'no_admission': 'No Admission of Liability - This settlement does not constitute an admission of wrongdoing by either party',
    'confidentiality': 'Confidentiality Agreement - Terms of this settlement remain confidential between the parties',
    'future_work_clause': 'Future Business Relationship - Parties agree to maintain a professional relationship for potential future work',
    'late_payment_penalty_5pct': 'Late Payment Penalty - 5% penalty applies if payments are not made on schedule'
  };
  return termNames[term] || term;
}

export default SettlementReviewPage;
